import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { RouteReuseStrategy, Router, provideRouter, withPreloading, NoPreloading } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { environment, firebaseConfig, VERSION } from './environments/environment';
import { AppComponent } from './app/app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CacheInterceptor } from './app/services/cache.interceptor';
import { ResponseValidationInterceptor } from './app/services/response-validation.interceptor';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { shareReplay } from 'rxjs/operators';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app/app-routing.module';
import { Storage } from '@ionic/storage-angular';
import { initAnalytics } from './app/shared/analytics';
import {
  shouldAutoSignInToFirebaseEmulator,
  shouldInitializeRemoteTelemetry,
  shouldUseFirebaseEmulators,
} from './app/shared/firebase-emulator-mode';
import { waitForVerifiedAuthUser } from './app/shared/firebase-auth-ready';
import { resolveAngularEmulatorPorts } from './app/utils/worktree-emulators';

// Firebase imports
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import {
  Auth,
  provideAuth,
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
} from '@angular/fire/auth';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
  const loader = new TranslateHttpLoader(http, './assets/i18n/', '.json');
  const cache = new Map<string, ReturnType<typeof loader.getTranslation>>();

  // Wrap with in-memory cache using shareReplay to avoid duplicate requests per session
  return {
    getTranslation(lang: string) {
      if (!cache.has(lang)) {
        cache.set(lang, loader.getTranslation(lang).pipe(shareReplay(1)));
      }
      return cache.get(lang)!;
    },
  } as TranslateLoader;
}
import 'zone.js';

if (environment.production) {
  enableProdMode();
}

const remoteTelemetryEnabled = shouldInitializeRemoteTelemetry(getFirebaseEmulatorModeInput());
if (remoteTelemetryEnabled) initAnalytics();

// Initialize GlitchTip (Sentry-compatible) early at bootstrap
// User context will be set after login via LazyGlitchTipService.setUserContext()
if (remoteTelemetryEnabled && environment.production && environment.glitchtip?.dsn) {
  Sentry.init({
    dsn: environment.glitchtip.dsn,
    environment: environment.glitchtip.environment,
    release: VERSION,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: ['localhost', /^https:\/\/work\.multitan\.co\.il/],
        routingInstrumentation: Sentry.routingInstrumentation,
      }),
    ],
    tracesSampleRate: 0.1,
    tracesSampler: (ctx) => {
      const name = (ctx.transactionContext?.name || '').toLowerCase();
      if (name.includes('image') || name.includes('report') || name.includes('store_report')) return 0.25;
      if (name.includes('preload') || name.includes('background')) return 0.02;
      return 0.1;
    },
    ignoreErrors: [
      'AbortError',
      'The user aborted a request',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Navigation canceled',
      'ChunkLoadError',
    ],
    beforeSend(event, hint) {
      const originalException = hint?.originalException as any;
      const msg = (event.message || originalException?.message || '').toString();
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const tags = event.tags || {};
      const isUploadEvent =
        tags.error_type === 'image_upload' ||
        tags.context === 'media_upload' ||
        tags.context === 'media_upload_with_progress' ||
        tags.operation === 'media_upload' ||
        tags.operation === 'media_upload_progress';

      if (!isUploadEvent && offline) return null;
      if (!isUploadEvent && /NetworkError|Failed to fetch|ERR_NETWORK/.test(msg)) return null;
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') return null;
      return breadcrumb;
    },
    maxBreadcrumbs: 50,
  });

  // Baseline tags used for cross-surface triage consistency.
  Sentry.setTag('environment', environment.glitchtip.environment);
  Sentry.setTag('release', VERSION);

  // Additional global error handlers for edge cases outside Angular zone
  // These complement Sentry's automatic error handling
  if (typeof window !== 'undefined') {
    // Global window error handler (catches errors outside Angular zone)
    window.addEventListener('error', (event) => {
      // Skip if Sentry already captured or if it's a known noise error
      if (event.error) {
        const errorMsg = event.error?.message || event.error?.toString() || '';
        const isNoiseError = ['ResizeObserver', 'AbortError', 'ChunkLoadError', 'Navigation canceled'].some((noise) =>
          errorMsg.includes(noise),
        );

        if (!isNoiseError && typeof Sentry !== 'undefined' && Sentry.captureException) {
          Sentry.withScope((scope) => {
            scope.setTag('operation', 'global_window_error');
            scope.setTag('error_source', 'global_window_error');
            scope.setTag('error_type', 'unhandled_exception');
            scope.setContext('window_error', {
              message: event.message,
              filename: event.filename,
              lineno: event.lineno,
              colno: event.colno,
              error: event.error?.message,
            });
            Sentry.captureException(event.error);
          });
        }
      }
    });

    // Unhandled promise rejection handler (catches async errors)
    window.addEventListener('unhandledrejection', (event) => {
      // Skip if Sentry already captured or if it's a known noise error
      const reason = event.reason;
      const errorMsg = reason?.message || reason?.toString() || '';

      const isNoiseError = ['AbortError', 'Navigation canceled', 'Failed to fetch', 'NetworkError'].some((noise) =>
        errorMsg.includes(noise),
      );

      if (!isNoiseError && typeof Sentry !== 'undefined' && Sentry.captureException) {
        Sentry.withScope((scope) => {
          scope.setTag('operation', 'unhandled_promise_rejection');
          scope.setTag('error_source', 'unhandled_promise_rejection');
          scope.setTag('error_type', 'promise_rejection');
          scope.setContext('promise_rejection', {
            reason: errorMsg,
            promise: event.promise?.toString(),
          });

          // Capture as exception if it's an Error object, otherwise as message
          if (reason instanceof Error) {
            Sentry.captureException(reason);
          } else {
            scope.setContext('rejection_details', {
              errorMsg,
              promise: event.promise?.toString(),
            });
            Sentry.captureMessage('Unhandled promise rejection', 'error');
          }
        });
      }
    });
  }

  // Initialize monitoring services
  // These are imported and initialized after bootstrap to avoid circular dependencies
  import('./app/services/app-monitoring.service').then(({ AppMonitoringService }) => {
    const monitoring = new AppMonitoringService();
    monitoring.initializeMonitoring();
  });

  import('./app/services/sw-monitoring.service').then(({ SwMonitoringService }) => {
    const swMonitoring = new SwMonitoringService();
    swMonitoring.initializeMonitoring();
  });
}

// Function to check if we should use emulator
// Checks URL param (?emulate) and localStorage flag (set by E2E storageState)
function getFirebaseEmulatorModeInput() {
  return {
    production: environment.production,
    search: typeof window === 'undefined' ? '' : window.location.search,
    storedPreference: typeof window === 'undefined' ? null : localStorage.getItem('__e2e_emulate__'),
  };
}

function shouldUseEmulator(): boolean {
  return shouldUseFirebaseEmulators(getFirebaseEmulatorModeInput());
}

async function initializeFirebaseEmulatorSession(): Promise<void> {
  const useFirebaseEmulators = shouldUseEmulator();
  const shouldAutoSignIn = shouldAutoSignInToFirebaseEmulator({
    hostname: typeof window === 'undefined' ? '' : window.location.hostname,
    search: typeof window === 'undefined' ? '' : window.location.search,
    useFirebaseEmulators,
  });
  if (!shouldAutoSignIn) return;

  const auth = inject(Auth);
  if (auth.currentUser?.emailVerified) return;

  const idToken = JSON.stringify({
    sub: 'angular-agent-local',
    email: 'angular-agent@local.reportan',
    email_verified: true,
    name: 'Angular Agent',
  });
  const credential = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
  await waitForVerifiedAuthUser(credential.user.uid, (next, error) =>
    onAuthStateChanged(auth, next, error),
  );
  localStorage.setItem('__e2e_emulate__', 'true');
}

const emulatorPorts = resolveAngularEmulatorPorts(
  typeof window === 'undefined' ? 4200 : Number(window.location.port),
);

// Function to initialize Firebase Functions with emulator support
function initializeFunctions() {
  const functions = getFunctions(undefined, 'europe-west1');
  if (shouldUseEmulator()) {
    connectFunctionsEmulator(functions, 'localhost', emulatorPorts.functions);
  }
  return functions;
}

bootstrapApplication(AppComponent, {
  providers: [
    // Explicitly provide DOCUMENT for Ionic Platform (some builds/packaging can omit it)
    { provide: DOCUMENT, useFactory: () => document },
    importProvidersFrom(
      BrowserModule,
      IonicStorageModule.forRoot({
        name: 'multitan-reports',
        driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        defaultLanguage: 'he',
      }),
    ),
    // Router configuration
    // Use NoPreloading strategy to defer loading of feature modules
    // until the user navigates to them. This reduces the initial
    // bundle size and speeds up first load.
    provideRouter(routes, withPreloading(NoPreloading)),
    // HTTP configuration
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ResponseValidationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    // Ionic configuration
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // Firebase configuration
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideAuth(() => {
      const auth = getAuth();
      if (shouldUseEmulator()) {
        connectAuthEmulator(auth, `http://localhost:${emulatorPorts.auth}`);
        // Expose sign-in for E2E tests (only in emulator mode)
        (window as any).__e2e_signIn__ = async (email: string, password: string) => {
          const credential = await signInWithEmailAndPassword(auth, email, password);
          await waitForVerifiedAuthUser(credential.user.uid, (next, error) =>
            onAuthStateChanged(auth, next, error),
          );
        };
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (shouldUseEmulator()) {
        connectFirestoreEmulator(firestore, 'localhost', emulatorPorts.firestore);
      }
      return firestore;
    }),
    provideDatabase(() => getDatabase()),
    provideFunctions(() => initializeFunctions()),

    // Other providers
    StatusBar,
    SplashScreen,
    provideAnimations(),
    // Angular error handler using GlitchTip (Sentry-compatible SDK)
    // Sentry is initialized early at bootstrap; user context set after login
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(initializeFirebaseEmulatorSession),
    provideIonicAngular({
      innerHTMLTemplatesEnabled: true,
    }),
  ],
}).catch((err) => {
  // Critical bootstrap error handling
  console.error('Bootstrap failed:', err);

  // Capture bootstrap errors to Sentry if available
  if (
    remoteTelemetryEnabled &&
    environment.production &&
    environment.glitchtip?.dsn &&
    typeof Sentry !== 'undefined' &&
    Sentry.captureException
  ) {
    Sentry.withScope((scope) => {
      scope.setTag('operation', 'bootstrap_failure');
      scope.setTag('error_source', 'bootstrap');
      scope.setTag('error_type', 'bootstrap_failure');
      scope.setLevel('fatal');
      scope.setContext('bootstrap_error', {
        error: err?.message || err?.toString(),
        stack: err?.stack,
      });
      Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
    });
  }
});
